import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Button } from "@material-ui/core";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import Dialog from "../../waybee-ui/Dialog";
import DialogContent from "../../waybee-ui/Dialog/DialogContent";

const DefaultBar = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background-color: white;
  border-right: 1px solid #e4e3e2;
  border-top: 1px solid #e4e3e2;
  border-left: 1px solid #e4e3e2;
  padding: 6px 10px;
`;

const OpenButton = styled(Button)`
  && {
    text-transform: none;
    font-weight: 500;
  }
`;

const Placeholder = styled.div`
  min-height: ${({ $minHeight }) => $minHeight}px;
`;

const GridHost = styled.div`
  width: 100%;
  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1;
      min-height: 0;
      height: 100%;
      display: flex;
      flex-direction: column;

      & > * {
        flex: 1 1 auto;
        min-height: 0 !important;
        height: 100% !important;
      }
    `}
`;

const contentStyle = {
  flex: "1 1 auto",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  padding: 28
};

const paperProps = {
  style: {
    width: "95vw",
    height: "95vh",
    maxWidth: "95vw",
    maxHeight: "95vh",
    margin: 0,
    overflow: "visible",
    display: "flex",
    flexDirection: "column"
  }
};

const closeButtonPosition = {
  top: "-14px",
  right: "-14px"
};

const GridFullscreen = ({ children, minHeight, renderToolbar }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const trigger = (
    <OpenButton
      color="primary"
      startIcon={<FullscreenIcon />}
      onClick={handleOpen}
      disabled={open}
      aria-label={t("ui.grid.fullscreenAria")}
      title={t("ui.grid.fullscreen")}
    >
      {t("ui.grid.fullscreen")}
    </OpenButton>
  );

  const renderGrid = isFullscreen =>
    typeof children === "function"
      ? children({ isFullscreen })
      : children;

  return (
    <>
      {renderToolbar ? (
        renderToolbar(trigger)
      ) : (
        <DefaultBar>{trigger}</DefaultBar>
      )}
      {open ? (
        <Placeholder $minHeight={minHeight} aria-hidden />
      ) : (
        <GridHost>{renderGrid(false)}</GridHost>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        closeButton
        fullWidth
        maxWidth={false}
        scroll="paper"
        PaperProps={paperProps}
        closeButtonPosition={closeButtonPosition}
        aria-label={t("ui.grid.fullscreen")}
      >
        <DialogContent style={contentStyle}>
          {open ? <GridHost $fill>{renderGrid(true)}</GridHost> : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

GridFullscreen.propTypes = {
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
  minHeight: PropTypes.number,
  renderToolbar: PropTypes.func
};

GridFullscreen.defaultProps = {
  minHeight: 220,
  renderToolbar: undefined
};

export default GridFullscreen;
